import iso from 'iso-3166-1'
import { SelectOption } from '@ui/forms/Select/SelectProps'

type IsoCountry = {
  country: string
  alpha2: string
  alpha3: string
  numeric: string
}

const COUNTRY_CODES = (iso.all() as IsoCountry[]).map(({ alpha2 }) => alpha2)

/** Countries to show at the top of the list (then sorted by localized name). */
// Most common nationalities in Spain: Spain first, then top foreign nationalities by population (2024 data)
const COUNTRY_PRIORITY: string[] = [
  'ES', // Spain (host country)
  'MA', // Morocco - 956,403 residents
  'CO', // Colombia - 648,872 residents
  'RO', // Romania - 614,281 residents
  'VE', // Venezuela - 358,523 residents
  'IT', // Italy - 342,361 residents
  'GB', // United Kingdom - 268,191 residents
  'PE', // Peru - 248,791 residents
  'CN', // China - 234,870 residents
  'UA', // Ukraine - 203,534 residents
  'AR', // Argentina - 146,156 residents
  'EC', // Ecuador - 129,461 residents
  'FR', // France - 126,205 residents
  'PT', // Portugal - 109,547 residents
  'BR', // Brazil - 105,028 residents
]

/**
 * Country options with labels localized via Intl.DisplayNames (current locale).
 * Priority countries (COUNTRY_PRIORITY) appear first in exact order, then the rest sorted by localized label.
 */
export function getCountryOptions(
  locale: string,
  priorityCountries: string[] = COUNTRY_PRIORITY
): SelectOption<string>[] {
  const displayNames = new Intl.DisplayNames([locale], { type: 'region' })
  const byCode = new Map(
    COUNTRY_CODES.map((code) => [
      code,
      { value: code, label: displayNames.of(code) ?? code },
    ])
  )
  const prioritySet = new Set(priorityCountries)
  const priority = priorityCountries
    .filter((code) => byCode.has(code))
    .map((code) => byCode.get(code)!)
  const rest = COUNTRY_CODES.filter((code) => !prioritySet.has(code))
    .map((code) => byCode.get(code)!)
    .sort((a, b) => a.label.localeCompare(b.label, locale))
  return [...priority, ...rest]
}
